"use client";

import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import Link from "next/link";
import { useQuery } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import { ImageOff } from "lucide-react";
import { getNftDetail } from "@/features/asset/api";
import type { NftItem } from "@/features/nfts/types";
import { sanitizeUrl } from "@/lib/helpers";
import { profileImageUrl } from "@/lib/profile-image";
import { listingCurrencySymbol } from "@/features/finance/listing-currency";
import { listedPrice, PriceRow, TokenTag } from "./ListingPrice";
import "./sections.css";
import "./nft-tile.css";
import { withPercent } from "@/lib/percent";
type Props = {
  item: NftItem;
  lang: "fa" | "en";
  noMedia: string;
  marked?: boolean;
  /** False where the page already names the maker above every card (a single collection). */
  showOwner?: boolean;
};
type MarketOwner = {
  name?: string | null;
  username?: string | null;
  profile?: {
    cover?: string | null;
  } | null;
};
function firstOwner(value: unknown): MarketOwner | null {
  const candidate = Array.isArray(value) ? value[0] : value;
  return candidate && typeof candidate === "object" ? candidate as MarketOwner : null;
}
function initials(name: string) {
  const first = Array.from(name.replace(/^NB-User-#?/i, "").trim())[0];
  return first ? first.toLocaleUpperCase() : "?";
}
function networkLabel(network: string | null | undefined, isFa: boolean) {
  const key = network?.trim().toLowerCase();
  if (key === "polygon" || key === "matic") return isFa ? "پالیگان" : "Polygon";
  if (key === "ethereum" || key === "eth") return isFa ? "اتریوم" : "Ethereum";
  return network || "Ethereum";
}

/** The market uses the same artwork card as Home's latest-work shelf. Browse
 * context changes the grid and controls, never the visual language of a work. */
export default function NftTile(t0) {
  const $ = _c(74);
  const {
    item,
    lang,
    noMedia,
    marked: t1,
    showOwner: t2
  } = t0;
  const marked = t1 === undefined ? false : t1;
  const showOwner = t2 === undefined ? true : t2;
  const isFa = lang === "fa";
  const number = new Intl.NumberFormat(isFa ? "fa-IR" : "en-US");
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      triggerOnce: true,
      rootMargin: "240px"
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const {
    ref,
    inView
  } = useInView(t3);
  const listedOwner = firstOwner(item.user);
  const canResolveOwner = Boolean(item.contract_address && item.token_id != null);
  let t4;
  if ($[1] !== item.id) {
    t4 = ["nft-tile-detail", item.id];
    $[1] = item.id;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== item.contract_address || $[4] !== item.token_id || $[5] !== lang) {
    t5 = async () => {
      const response = await getNftDetail(item.contract_address, String(item.token_id), lang);
      return response.data?.item ?? null;
    };
    $[3] = item.contract_address;
    $[4] = item.token_id;
    $[5] = lang;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = inView && canResolveOwner;
  let t7;
  if ($[7] !== t4 || $[8] !== t5 || $[9] !== t6) {
    t7 = {
      queryKey: t4,
      queryFn: t5,
      enabled: t6,
      staleTime: 900000,
      gcTime: 1800000,
      retry: 1
    };
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const {
    data: detail,
    isPending
  } = useQuery(t7);
  const owner = firstOwner(detail?.user) || listedOwner;
  const ownerName = owner?.name || owner?.username || (isPending && canResolveOwner ? isFa ? "\u062F\u0631 \u062D\u0627\u0644 \u062F\u0631\u06CC\u0627\u0641\u062A \u0647\u0646\u0631\u0645\u0646\u062F" : "Loading artist" : isFa ? "\u0647\u0646\u0631\u0645\u0646\u062F \u0646\u0627\u0634\u0646\u0627\u0633" : "Unknown artist");
  const ownerUsername = typeof owner?.username === "string" ? owner.username.trim() || null : null;
  let t8;
  if ($[11] !== item.image) {
    t8 = sanitizeUrl(item.image ?? undefined) || undefined;
    $[11] = item.image;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const image = t8;
  const avatar = profileImageUrl(owner?.profile?.cover) || undefined;
  let t9;
  if ($[13] !== detail?.listing || $[14] !== item.listing) {
    t9 = listingCurrencySymbol(detail?.listing) ?? listingCurrencySymbol(item.listing);
    $[13] = detail?.listing;
    $[14] = item.listing;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const listedCurrency = t9;
  const t10 = detail ? detail.listing : item.listing;
  let t11;
  if ($[16] !== isFa || $[17] !== t10) {
    t11 = listedPrice(t10, isFa);
    $[16] = isFa;
    $[17] = t10;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const price = t11;
  const href = `/${lang}/assets/${item.network_name || "ethereum"}/${item.contract_address}/${item.token_id}`;
  const t12 = `v2-tile v2-nft-tile${marked ? " v2-tile--marked" : ""}`;
  let t13;
  if ($[19] !== item.name) {
    t13 = <span className="v2-sr-only">{item.name}</span>;
    $[19] = item.name;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== href || $[22] !== item.name || $[23] !== t13) {
    t14 = <Link href={href} className="v2-tile-link v2-focus" aria-label={item.name}>{t13}</Link>;
    $[21] = href;
    $[22] = item.name;
    $[23] = t13;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== image || $[26] !== noMedia) {
    t15 = image ? <Image src={image} alt="" fill={true} sizes="(max-width: 619px) 50vw, (max-width: 1023px) 33vw, (max-width: 1439px) 25vw, 20vw" /> : <span className="v2-no-media"><ImageOff aria-hidden={true} /><span>{noMedia}</span></span>;
    $[25] = image;
    $[26] = noMedia;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== item.network_name) {
    t16 = item.network_name?.toLowerCase();
    $[28] = item.network_name;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== isFa || $[31] !== item.network_name) {
    t17 = networkLabel(item.network_name, isFa);
    $[30] = isFa;
    $[31] = item.network_name;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== t16 || $[34] !== t17) {
    t18 = <span className="v2-tag v2-nft-net" data-chain={t16}>{t17}</span>;
    $[33] = t16;
    $[34] = t17;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== isFa || $[37] !== item.token_id || $[38] !== listedCurrency || $[39] !== price) {
    t19 = price ? <TokenTag id={item.token_id} /> : listedCurrency ? <span className="v2-tag v2-nft-cur" aria-label={isFa ? `ارز فروش: ${listedCurrency}` : `Listing currency: ${listedCurrency}`}>{listedCurrency}</span> : null;
    $[36] = isFa;
    $[37] = item.token_id;
    $[38] = listedCurrency;
    $[39] = price;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== t15 || $[42] !== t18 || $[43] !== t19) {
    t20 = <div className="v2-tile-media">{t15}{t18}{t19}</div>;
    $[41] = t15;
    $[42] = t18;
    $[43] = t19;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  const t21 = "v2-tile-body";
  let t22;
  if ($[45] !== item.name) {
    t22 = <span className="v2-tile-name" title={item.name}>{item.name}</span>;
    $[45] = item.name;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  const t23 = "v2-tile-meta";
  const t24 = showOwner ? undefined : true;
  const t25 = !showOwner ? null : ownerUsername ? <Link className="v2-creator v2-focus" href={`/${lang}/artists/${ownerUsername}`}>{avatar ? <Image src={avatar} alt="" width={18} height={18} /> : <span className="v2-creator-ini" aria-hidden={true}>{initials(ownerName)}</span>}<span>{ownerName}</span></Link> : <span className="v2-creator" aria-label={ownerName}><span className="v2-creator-ini" aria-hidden={true}>{initials(ownerName)}</span><span>{ownerName}</span></span>;
  let t26;
  if ($[47] !== item.network_name) {
    t26 = item.network_name?.toLowerCase();
    $[47] = item.network_name;
    $[48] = t26;
  } else {
    t26 = $[48];
  }
  let t27;
  if ($[49] !== isFa || $[50] !== item.network_name) {
    t27 = networkLabel(item.network_name, isFa);
    $[49] = isFa;
    $[50] = item.network_name;
    $[51] = t27;
  } else {
    t27 = $[51];
  }
  let t28;
  if ($[52] !== t26 || $[53] !== t27) {
    t28 = <span className="v2-chip v2-chip--chain" data-chain={t26}>{t27}</span>;
    $[52] = t26;
    $[53] = t27;
    $[54] = t28;
  } else {
    t28 = $[54];
  }
  let t29;
  if ($[55] !== isFa || $[56] !== listedCurrency || $[57] !== price) {
    t29 = listedCurrency && !price ? <span className="v2-chip v2-chip--mono" aria-label={isFa ? `ارز فروش: ${listedCurrency}` : `Listing currency: ${listedCurrency}`}>{listedCurrency}</span> : null;
    $[55] = isFa;
    $[56] = listedCurrency;
    $[57] = price;
    $[58] = t29;
  } else {
    t29 = $[58];
  }
  let t30;
  if ($[59] !== t24 || $[60] !== t25 || $[61] !== t28 || $[62] !== t29) {
    t30 = <div className={t23} data-chips-only={t24}>{t25}{t28}{t29}</div>;
    $[59] = t24;
    $[60] = t25;
    $[61] = t28;
    $[62] = t29;
    $[63] = t30;
  } else {
    t30 = $[63];
  }
  const t31 = price ? <PriceRow price={{
    text: price.text,
    symbol: price.symbol ?? listedCurrency
  }} isFa={isFa} /> : <div className="v2-tile-meta"><span className="v2-card-facts">{Number(item.standard) === 1155 && Number(item.quantity) > 1 ? <span className="v2-chip">{number.format(Number(item.quantity))} {isFa ? "\u0646\u0633\u062E\u0647" : "editions"}</span> : <span className="v2-chip v2-chip--mono">{Number(item.standard) === 1155 ? "1155" : "721"}</span>}<span className="v2-token">#{item.token_id}</span></span>{Number(item.royalty) > 0 ? <span className="v2-royalty"><span>{isFa ? "\u0631\u0648\u06CC\u0627\u0644\u062A\u06CC" : "Royalty"}</span><b>{withPercent(number.format(Number(item.royalty)), isFa)}</b></span> : null}</div>;
  let t32;
  if ($[64] !== t22 || $[65] !== t30 || $[66] !== t31) {
    t32 = <div className={t21}>{t22}{t30}{t31}</div>;
    $[64] = t22;
    $[65] = t30;
    $[66] = t31;
    $[67] = t32;
  } else {
    t32 = $[67];
  }
  let t33;
  if ($[68] !== ref || $[69] !== t12 || $[70] !== t14 || $[71] !== t20 || $[72] !== t32) {
    t33 = <article ref={ref} className={t12}>{t14}{t20}{t32}</article>;
    $[68] = ref;
    $[69] = t12;
    $[70] = t14;
    $[71] = t20;
    $[72] = t32;
    $[73] = t33;
  } else {
    t33 = $[73];
  }
  return t33;
}